// Wires the app state to the map view and the view's pointer events back to the actions.
//
//   state.snapshot ────► view.setSnapshot        state.selection ──► view.highlight
//   state.lensResult ──► view.setLens            view.onHover ─────► actions.hover
//   state.mode ────────► view.setMode            view.onClick ─────► actions.select
//   state.camera ──────► view.flyTo (once per request seq)
//
// Only the composition root knows both sides; neither layer imports the other.

import { effect } from '@preact/signals';
import type { Services } from '../services/api.ts';
import type { MapView } from '../view/api.ts';

/** Connect `view` to `services`; returns a function that removes the state subscriptions. */
export function bindView(view: MapView, services: Services): () => void {
  const { state, actions } = services;
  let lastRequest = 0;

  const stops = [
    // Snapshot and lens re-derive when the date, leader or lens changes.
    effect(() => {
      const snapshot = state.snapshot.value;
      if (snapshot) {
        view.setSnapshot(snapshot);
      }
    }),

    effect(() => view.setLens(state.lensResult.value)),

    effect(() => view.setMode(state.mode.value)),

    // A camera request is acted on once: a re-run with the same seq must not fly again.
    effect(() => {
      const request = state.camera.value;
      if (!request || request.seq === lastRequest) {
        return;
      }
      lastRequest = request.seq;
      view.flyTo(request.hex, request.zoom);
    }),

    effect(() => {
      const selected = state.selection.value;
      view.highlight(selected ? [selected.hex] : []);
    }),
  ];

  // The view offers no unsubscribe; these callbacks live as long as the view does.
  view.onHover((pick) => actions.hover(pick));
  view.onClick((pick) => actions.select(pick.hex));

  return () => stops.forEach((stop) => stop());
}
